import { Dispatch, useCallback } from 'react';
import { Conversation, CreateConversationData } from '../schemas/chat';
import { ChatAction } from '../contexts/chatReducer';
import chatService from '../services/chatService';
import * as chatStorage from '../storage/chatStorage';

interface UseConversationActionsParams {
  dispatch: Dispatch<ChatAction>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

export const useConversationActions = ({
  dispatch,
  setLoading,
  setError,
}: UseConversationActionsParams) => {
  const loadConversations = useCallback(async () => {
    try {
      // ========================================
      // 1. 初始化：设置加载状态
      // ========================================
      setLoading(true);

      // ========================================
      // 2. 检查认证状态
      // ========================================
      if (!chatService.isAuthed()) {
        dispatch({ type: 'SET_CONVERSATIONS', payload: [] });
        return;
      }

      // ========================================
      // 3. 获取对话列表
      // ========================================
      const response = await chatService.getConversations();
      const conversations = Array.isArray(response) ? response : response.items || [];

      // ========================================
      // 4. 更新状态
      // ========================================
      dispatch({ type: 'SET_CONVERSATIONS', payload: conversations });
    } catch (error) {
      // ========================================
      // 5. 错误处理
      // ========================================
      if (error instanceof Error && error.message.includes('Not authenticated')) {
        dispatch({ type: 'SET_CONVERSATIONS', payload: [] });
      } else {
        setError(error instanceof Error ? error.message : 'Failed to load conversations');
      }
    } finally {
      setLoading(false);
    }
  }, [dispatch, setLoading, setError]);

  const createConversation = useCallback(
    async (data?: CreateConversationData) => {
      try {
        // ========================================
        // 1. 初始化：设置加载状态
        // ========================================
        setLoading(true);

        // ========================================
        // 2. 创建对话
        // ========================================
        const conversation = await chatService.createConversation(data || {});

        // ========================================
        // 3. 更新状态
        // ========================================
        dispatch({ type: 'ADD_CONVERSATION', payload: conversation });
        dispatch({ type: 'SET_CURRENT_CONVERSATION', payload: conversation.id });

        return conversation;
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Failed to create conversation');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [dispatch, setLoading, setError]
  );

  const updateConversation = useCallback(
    async (id: string, data: Partial<Conversation>) => {
      try {
        // ========================================
        // 1. 更新对话
        // ========================================
        const updatedConversation = await chatService.updateConversation(id, data);

        // ========================================
        // 2. 更新状态
        // ========================================
        dispatch({ type: 'UPDATE_CONVERSATION', payload: updatedConversation });
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Failed to update conversation');
      }
    },
    [dispatch, setError]
  );

  const deleteConversation = useCallback(
    async (id: string) => {
      try {
        // ========================================
        // 1. 删除对话
        // ========================================
        await chatService.deleteConversation(id);

        // ========================================
        // 2. 清理 IndexedDB 缓存 (新增)
        // ========================================
        await chatStorage.deleteMessages(id);

        // ========================================
        // 3. 更新状态
        // ========================================
        dispatch({ type: 'DELETE_CONVERSATION', payload: id });
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Failed to delete conversation');
      }
    },
    [dispatch, setError]
  );

  const setCurrentConversation = useCallback(
    (id: string | null) => {
      dispatch({ type: 'SET_CURRENT_CONVERSATION', payload: id });
    },
    [dispatch]
  );

  const archiveConversation = useCallback(
    async (id: string) => {
      try {
        // ========================================
        // 1. 归档对话
        // ========================================
        const archivedConversation = await chatService.archiveConversation(id);

        // ========================================
        // 2. 更新状态
        // ========================================
        dispatch({ type: 'UPDATE_CONVERSATION', payload: archivedConversation });
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Failed to archive conversation');
      }
    },
    [dispatch, setError]
  );

  const pinConversation = useCallback(
    async (id: string, pinned: boolean = true) => {
      try {
        // ========================================
        // 1. 固定/取消固定对话
        // ========================================
        const updatedConversation = await chatService.pinConversation(id, pinned);

        // ========================================
        // 2. 更新状态
        // ========================================
        dispatch({ type: 'UPDATE_CONVERSATION', payload: updatedConversation });
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Failed to pin conversation');
      }
    },
    [dispatch, setError]
  );

  const loadConversationWithMessages = useCallback(
    async (id: string) => {
      try {
        // ========================================
        // 1. 乐观加载：尝试从 IndexedDB 缓存加载消息（仅消息，不获取对话详情）
        // ========================================
        const cachedMessages = await chatStorage.getMessages(id);
        if (cachedMessages && cachedMessages.length > 0) {
          console.log(
            `[loadConversationWithMessages] Loaded ${cachedMessages.length} messages from cache for ${id}`
          );

          // 仅派发缓存的消息，不获取对话详情以避免竞态条件和冗余 API 调用
          dispatch({
            type: 'UPDATE_CONVERSATION',
            payload: { id, messages: cachedMessages } as any,
          });
        }

        // ========================================
        // 2. 从 API 同步最新数据（始终执行）
        // ========================================
        const [conversation, messages] = await Promise.all([
          chatService.getConversationById(id),
          chatService.getMessages(id),
        ]);

        // ========================================
        // 3. 缓存消息到 IndexedDB
        // ========================================
        await chatStorage.saveMessages(id, messages);

        // ========================================
        // 4. 将消息添加到对话对象中
        // ========================================
        const conversationWithMessages: Conversation = {
          ...conversation,
          messages: messages,
        };

        // ========================================
        // 5. 更新状态（API 数据覆盖缓存）
        // ========================================
        dispatch({ type: 'UPDATE_CONVERSATION', payload: conversationWithMessages });
        dispatch({ type: 'SET_CURRENT_CONVERSATION', payload: conversationWithMessages.id });

        return conversationWithMessages;
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Failed to load conversation');
        return null;
      }
    },
    [dispatch, setError]
  );

  return {
    loadConversations,
    createConversation,
    updateConversation,
    deleteConversation,
    setCurrentConversation,
    archiveConversation,
    pinConversation,
    loadConversationWithMessages,
  };
};
